import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Mail, Trophy, Calendar, User, Dog, Users, Shield, Store, Award, LogOut } from 'lucide-react';
import ContentManager from '../components/admin/ContentManager';
import AdminLogin from '../components/admin/AdminLogin';
import { authFetch, verifyToken, clearAuth, getAdminEmail } from '../lib/auth';

// Module-level constants — declared once, stable identity across renders.
const COMMITTEE_FIELDS = [
  { name: 'name', label: 'Name', type: 'text', required: true },
  { name: 'role', label: 'Role (optional)', type: 'text' },
  { name: 'bio', label: 'Short bio (optional)', type: 'textarea' },
  { name: 'order', label: 'Order', type: 'number', default: 0 },
];

const HERO_MASTER_FIELDS = [
  { name: 'name', label: 'Name', type: 'text', required: true },
  { name: 'category', label: 'Category', type: 'select', options: ['Education & Therapy', 'Conservation', 'Law Enforcement', 'Assistance'] },
  { name: 'description', label: 'Short description', type: 'textarea' },
  { name: 'full_description', label: 'Full description', type: 'textarea' },
  { name: 'website', label: 'Website URL', type: 'text' },
  { name: 'instagram', label: 'Instagram URL', type: 'text' },
  { name: 'facebook', label: 'Facebook URL', type: 'text' },
  { name: 'image_url', label: 'Hero Master Image (logo or team photo)', type: 'image' },
  { name: 'order', label: 'Order', type: 'number', default: 0 },
];

const VENDOR_FIELDS = [
  { name: 'name', label: 'Name', type: 'text', required: true },
  { name: 'section', label: 'Section', type: 'select', required: true, options: ['Food & Refreshments', 'Service Dog Groups', 'Health & Safety', 'Retail & Services'] },
  { name: 'subtitle', label: 'Subtitle (optional)', type: 'text' },
  { name: 'description', label: 'Description', type: 'textarea' },
  { name: 'website', label: 'Website URL', type: 'text' },
  { name: 'facebook', label: 'Facebook URL', type: 'text' },
  { name: 'instagram', label: 'Instagram URL', type: 'text' },
  { name: 'image_url', label: 'Vendor Image (logo or product photo)', type: 'image' },
  { name: 'also_supporter', label: 'Also list on Sponsors and Supporters page', type: 'checkbox', default: false },
  { name: 'coming_soon', label: 'Show as "Coming Soon"', type: 'checkbox', default: false },
  { name: 'order', label: 'Order', type: 'number', default: 0 },
];

const PARTNER_FIELDS = [
  { name: 'name', label: 'Name', type: 'text', required: true },
  { name: 'section', label: 'Type', type: 'select', required: true, options: ['Sponsors', 'Supporters'] },
  { name: 'subtitle', label: 'Subtitle (optional)', type: 'text' },
  { name: 'description', label: 'Description', type: 'textarea' },
  { name: 'website', label: 'Website URL', type: 'text' },
  { name: 'facebook', label: 'Facebook URL', type: 'text' },
  { name: 'instagram', label: 'Instagram URL', type: 'text' },
  { name: 'image_url', label: 'Logo or photo', type: 'image' },
  { name: 'vendor_section', label: 'Also list on Vendors page as (optional)', type: 'select', options: ['', 'Food & Refreshments', 'Service Dog Groups', 'Health & Safety', 'Retail & Services'] },
  { name: 'coming_soon', label: 'Show as "Coming Soon"', type: 'checkbox', default: false },
  { name: 'order', label: 'Order', type: 'number', default: 0 },
];

const PARTNER_SECTIONS = ['Sponsors', 'Supporters'];
const isPartner = (v) => PARTNER_SECTIONS.includes(v.section);
const isVendor = (v) => !PARTNER_SECTIONS.includes(v.section);

const AdminDashboard = ({ onLogout }) => {
  const [contactMessages, setContactMessages] = useState([]);
  const [competitionEntries, setCompetitionEntries] = useState([]);
  const [stats, setStats] = useState({ total_entries: 0, by_competition: [] });
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [contactRes, entriesRes, statsRes] = await Promise.all([
        authFetch('/api/contact/messages?limit=50'),
        authFetch('/api/competitions/entries?limit=100'),
        authFetch('/api/competitions/stats'),
      ]);
      if (contactRes.status === 401 || entriesRes.status === 401 || statsRes.status === 401) {
        onLogout();
        return;
      }
      if (contactRes.ok) setContactMessages(await contactRes.json());
      if (entriesRes.ok) setCompetitionEntries(await entriesRes.json());
      if (statsRes.ok) setStats(await statsRes.json());
    } catch (err) {
      // Network error — leave previous data in place. Log for diagnostics in dev only.
      if (process.env.NODE_ENV !== 'production') {
        console.error('[admin] Failed to fetch dashboard data:', err);
      }
    } finally {
      setLoading(false);
    }
  }, [onLogout]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatDate = (dateString) => new Date(dateString).toLocaleString('en-AU', {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <p className="text-lg text-slate-600">Loading dashboard…</p>
      </div>
    );
  }

  const adminEmail = getAdminEmail();

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 mb-2">Admin Dashboard</h1>
            <p className="text-lg text-slate-600">Manage submissions, entries, and editable site content for Paws in the Park 2026</p>
            {adminEmail && (
              <p className="text-sm text-slate-500 mt-1">Signed in as <span className="font-semibold">{adminEmail}</span></p>
            )}
          </div>
          <Button variant="outline" onClick={onLogout} data-testid="admin-logout-btn">
            <LogOut className="h-4 w-4 mr-2" />
            Sign Out
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="border-cyan-200 bg-gradient-to-br from-cyan-50 to-white">
            <CardHeader className="pb-3"><CardTitle className="text-sm font-medium text-slate-600">Contact Messages</CardTitle></CardHeader>
            <CardContent>
              <div className="flex items-center space-x-3">
                <Mail className="h-8 w-8 text-cyan-600" />
                <span className="text-3xl font-bold text-slate-900">{contactMessages.length}</span>
              </div>
            </CardContent>
          </Card>
          <Card className="border-teal-200 bg-gradient-to-br from-teal-50 to-white">
            <CardHeader className="pb-3"><CardTitle className="text-sm font-medium text-slate-600">Competition Entries</CardTitle></CardHeader>
            <CardContent>
              <div className="flex items-center space-x-3">
                <Trophy className="h-8 w-8 text-teal-600" />
                <span className="text-3xl font-bold text-slate-900">{stats.total_entries}</span>
              </div>
            </CardContent>
          </Card>
          <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-white">
            <CardHeader className="pb-3"><CardTitle className="text-sm font-medium text-slate-600">Most Popular Competition</CardTitle></CardHeader>
            <CardContent>
              <div className="flex items-center space-x-3">
                <Dog className="h-8 w-8 text-amber-600" />
                <span className="text-sm font-semibold text-slate-900">
                  {stats.by_competition[0]?.competition.substring(0, 20) || 'None yet'}
                </span>
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="contact" className="space-y-6">
          <TabsList className="grid w-full grid-cols-2 md:grid-cols-6 gap-1">
            <TabsTrigger value="contact" data-testid="admin-tab-contact"><Mail className="h-4 w-4 mr-1" />Messages</TabsTrigger>
            <TabsTrigger value="competitions" data-testid="admin-tab-competitions"><Trophy className="h-4 w-4 mr-1" />Entries</TabsTrigger>
            <TabsTrigger value="committee" data-testid="admin-tab-committee"><Users className="h-4 w-4 mr-1" />Committee</TabsTrigger>
            <TabsTrigger value="hero-masters" data-testid="admin-tab-hero-masters"><Shield className="h-4 w-4 mr-1" />Hero Masters</TabsTrigger>
            <TabsTrigger value="partners" data-testid="admin-tab-partners"><Award className="h-4 w-4 mr-1" />Sponsors and Supporters</TabsTrigger>
            <TabsTrigger value="vendors" data-testid="admin-tab-vendors"><Store className="h-4 w-4 mr-1" />Vendors</TabsTrigger>
          </TabsList>

          <TabsContent value="contact" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-semibold text-slate-900">Contact Messages</h2>
              <Button variant="outline" onClick={fetchData} data-testid="refresh-contact-btn">Refresh</Button>
            </div>
            {contactMessages.length === 0 ? (
              <Card><CardContent className="py-12 text-center">
                <Mail className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-600">No contact messages yet</p>
              </CardContent></Card>
            ) : (
              contactMessages.map((message) => (
                <Card key={message.id} className="border-slate-200 hover:border-cyan-300 transition-colors">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-xl flex items-center space-x-2">
                          <User className="h-5 w-5 text-cyan-600" />
                          <span>{message.name}</span>
                        </CardTitle>
                        <CardDescription className="mt-1">
                          <a href={`mailto:${message.email}`} className="text-cyan-600 hover:underline">{message.email}</a>
                          {message.phone && <span className="ml-2">• {message.phone}</span>}
                        </CardDescription>
                      </div>
                      <div className="flex flex-col items-end space-y-2">
                        <Badge variant="outline" className="capitalize">{message.subject}</Badge>
                        <span className="text-xs text-slate-500 flex items-center">
                          <Calendar className="h-3 w-3 mr-1" />
                          {formatDate(message.submitted_at)}
                        </span>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent><p className="text-slate-700 whitespace-pre-wrap">{message.message}</p></CardContent>
                </Card>
              ))
            )}
          </TabsContent>

          <TabsContent value="competitions" className="space-y-4">
            <h2 className="text-2xl font-semibold text-slate-900">Competition Entries</h2>
            {competitionEntries.length === 0 ? (
              <Card><CardContent className="py-12 text-center">
                <Trophy className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-600">No competition entries yet</p>
              </CardContent></Card>
            ) : (
              competitionEntries.map((entry) => (
                <Card key={entry.id} className="border-slate-200 hover:border-teal-300 transition-colors">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-xl flex items-center space-x-2">
                          <Dog className="h-5 w-5 text-teal-600" />
                          <span>{entry.dog_name}</span>
                          <span className="text-sm text-slate-500 font-normal">({entry.breed})</span>
                        </CardTitle>
                        <CardDescription className="mt-1">
                          Owner: {entry.owner_name} •{' '}
                          <a href={`mailto:${entry.email}`} className="text-cyan-600 hover:underline">{entry.email}</a>
                          {entry.phone && <span> • {entry.phone}</span>}
                        </CardDescription>
                      </div>
                      <span className="text-xs text-slate-500 flex items-center">
                        <Calendar className="h-3 w-3 mr-1" />
                        {formatDate(entry.submitted_at)}
                      </span>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex flex-wrap gap-2">
                      <Badge className="bg-teal-100 text-teal-800 hover:bg-teal-200">{entry.competition}</Badge>
                      {entry.category && <Badge variant="outline">{entry.category}</Badge>}
                      <Badge variant="outline" className="text-slate-600">Age: {entry.age}</Badge>
                    </div>
                    {entry.hero_story && (
                      <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                        <p className="font-semibold text-sm text-slate-700 mb-2">Hero Story:</p>
                        <p className="text-slate-700 text-sm whitespace-pre-wrap">{entry.hero_story}</p>
                      </div>
                    )}
                    {entry.special_needs && entry.special_needs !== 'None' && (
                      <div className="bg-amber-50 p-3 rounded-lg border border-amber-200">
                        <p className="font-semibold text-sm text-amber-900 mb-1">Special Needs:</p>
                        <p className="text-amber-800 text-sm">{entry.special_needs}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>

          <TabsContent value="committee">
            <ContentManager
              title="Committee Members"
              endpoint="committee"
              fields={COMMITTEE_FIELDS}
              onUnauthorized={onLogout}
            />
          </TabsContent>

          <TabsContent value="hero-masters">
            <ContentManager
              title="Hero Masters (Service Groups)"
              endpoint="hero-masters"
              fields={HERO_MASTER_FIELDS}
              onUnauthorized={onLogout}
            />
          </TabsContent>

          <TabsContent value="partners">
            <ContentManager
              title="Sponsors and Supporters"
              endpoint="vendors"
              fields={PARTNER_FIELDS}
              filter={isPartner}
              defaults={{ section: 'Sponsors' }}
              onUnauthorized={onLogout}
            />
          </TabsContent>

          <TabsContent value="vendors">
            <ContentManager
              title="Vendors"
              endpoint="vendors"
              fields={VENDOR_FIELDS}
              filter={isVendor}
              onUnauthorized={onLogout}
            />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

const Admin = () => {
  const [authStatus, setAuthStatus] = useState('checking'); // checking | authed | unauthed

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const ok = await verifyToken();
      if (!cancelled) setAuthStatus(ok ? 'authed' : 'unauthed');
    })();
    return () => { cancelled = true; };
  }, []);

  const handleLogout = () => {
    clearAuth();
    setAuthStatus('unauthed');
  };

  if (authStatus === 'checking') {
    return (
      <div className="min-h-[70vh] flex items-center justify-center">
        <p className="text-slate-600">Checking authentication…</p>
      </div>
    );
  }

  if (authStatus === 'unauthed') {
    return <AdminLogin onLogin={() => setAuthStatus('authed')} />;
  }

  return <AdminDashboard onLogout={handleLogout} />;
};

export default Admin;
